import { DeliverySiteId } from "~/assets/constants/ecomApi";
import { EcomService } from "~/services/ecom.service";
import type { NearestBranchRequest } from "~/types/ecom/branch.type";
import type {
  Product,
  ProductStockDetails,
} from "~/types/ecom/product/product.type";
import type {
  BranchWithStock,
  ProductStockResponse,
} from "~/types/ecom/product/stock.type";
import type { TrolleyChannel } from "~/types/ecom/trolley/trolley-map";

export const useStockStore = defineStore("stockStore", {
  state: () => ({
    branches_with_stock_data: <BranchWithStock[]>[],
    nearby_stock_search_branch_choice: <BranchWithStock | null>null,
    stock_finder_modal_visible: false,
    is_nearby_stock_search_loading: false,
    productResponse: null,
    checkStockModalVisible: false,
  }),
  getters: {},
  actions: {
    /* Get product details by codes */
    async getAllProducts(products: Product["code"][]) {
      try {
        const { data: productsResponse } = await EcomService.fetchAllProducts({
            products,
        });

        return productsResponse;
    } catch (error) {
        let errorMeta = {stockStoreError: true, products: products}
        const { $customClientErrorHandler } = useNuxtApp();
        $customClientErrorHandler( 
            error,
            'high',
            'stockStoreError - trying to get all Product using getAllProducts()',
            errorMeta
        );
        return error
        }
    },
    /* Return products with stock data injected */
    async getStockForProducts(products: Product["code"][]): Promise<Product[]> {
      if (!products.length) {
        throw new Error("failed to get stock - product codes array is empty");
      }
      try {
      // Fetch product details from the products API
      const productsResponse = await this.getAllProducts(products);

      // Fetch stock data from ECOM
      const updatedProducts = await this.fetchStockProduct(
        products,
        productsResponse
      );

      return updatedProducts;
    } catch (error) {
        const { $customClientErrorHandler } = useNuxtApp();
        const errorMeta = { stockStoreError: true, products };
        $customClientErrorHandler(
            error,
            'high',
            'stockStoreError - trying to get stock for products using getStockForProducts()',
            errorMeta
        );
        return error
    }
    },
    /* Availability evaluation */
    computeOutOfStockFlagValues(
      stockDetails: Product["stockDetails"],
      isBranchSet?: boolean
    ) {
      const availability = {
        outOfStockForDelivery: false,
        outOfStockForCollection: false,
      };

      // set delivery stock status
      if (!stockDetails?.delivery) availability.outOfStockForDelivery = true;

      // set collection stock status
      if (!stockDetails?.collection && isBranchSet)
        availability.outOfStockForCollection = true;

      return availability;
    },
    /* Transform the products array by injecting stock information */
    transformProducts(
      productsResponse: Product[],
      stockResponse: ProductStockResponse[],
      selectedBranchId?: string
    ): Product[] {
      return productsResponse.map((product) => {
        const productStockLevels = stockResponse.filter(
          (stockItem) => stockItem.product_code === product.code
        );
        const deliveryStockLevel = productStockLevels.find(
          (stockLevel) => stockLevel.site_id === DeliverySiteId
        );
        const collectionStockLevel = productStockLevels.find(
          (stockLevel) => stockLevel.site_id === selectedBranchId
        );
        // default values
        let direct_ship = false;
        let click_and_collect = true;
        let next_business_day_collection = true;
        let stockDetails: ProductStockDetails = { delivery: 0, collection: 0 };

        if (productStockLevels.length) {
          // set delivery stock
          if (deliveryStockLevel) {
            stockDetails.delivery = parseInt(deliveryStockLevel.stock_qty, 10);
            // set directship flag
            if (deliveryStockLevel.is_direct_ship) {
              direct_ship = true;
              click_and_collect = false;
              next_business_day_collection = false;
            }
          }
          // set collection stock
          if (collectionStockLevel)
            stockDetails.collection = parseInt(
              collectionStockLevel.stock_qty,
              10
            );
        }

        // ---- We should not alter this flag like below. In `getProducts` request it comes from the backend
        // const next_business_day_collection =  stockDetails.collection === 0 && !this.direct_ship;

        const { outOfStockForDelivery, outOfStockForCollection } =
          this.computeOutOfStockFlagValues(stockDetails, !!selectedBranchId);

        return {
          ...product,
          direct_ship,
          click_and_collect,
          next_business_day_collection,
          stockDetails,
          outOfStockForDelivery,
          outOfStockForCollection,
        };
      });
    },
    /* Get stock and Inject stock into product details */
    async fetchStockProduct(
      products: Product["code"][],
      productsResponse: Product[],nuxtApp = null,runtimeConfig = null
    ): Promise<Product[]> {
      try {
      let sites: string[] = [DeliverySiteId];
      const branchStore = useBranchStore();
      const selectedBranchId = branchStore.lastSavedBranch?.id;

      // Set branch details to fetch collection stock
      if (branchStore.is_branch_set) sites.push(selectedBranchId || "");

      const response = await EcomService.fetchStockProduct({ products, sites },nuxtApp,runtimeConfig);

      if (!response || !Array.isArray(response.data)) {
        return [];
      } 
      // Inject the stock data to the corresponding product in the productsResponse
      return this.transformProducts(
        productsResponse,
        response.data,
        selectedBranchId
      );
    } catch (error) {
      const { $customClientErrorHandler } = useNuxtApp();
      const errorMeta = {
          stockStoreError: true,
          products,
      };
      $customClientErrorHandler(
          error,
          'high',
          'stockStoreError - error in fetchStockProduct()',
          errorMeta
      );
      return error; 
    }
    },

    /* Find stock nearby feature */
    async getNearByStock(
      product_code: Product["code"],
      required_quantity: number,
      nearest_request: NearestBranchRequest
    ) {
      this.branches_with_stock_data = [];
      const { $customClientWarningHandler } = useNuxtApp();
      const warningMeta = {product_code, required_quantity, nearest_request, stockStoreWarn: true}
      try {
        // get nearest branches from the current branch and filter them
        const ecomService = new EcomService();

        const response = await ecomService.fetchNearestBranches(
          nearest_request
        );
        if (!response || !response.data || !response.data.length) {
          $customClientWarningHandler('high','no nearest branch found with requested stock',warningMeta);
        }

        // Get all branch IDs
        const allBranchIds = response.data.map((branch) => branch.id);

        // Fetch stock data from ECOM
        const { data: stockResponse } = await EcomService.fetchStockProduct({
          products: [product_code],
          sites: allBranchIds,
        });

        if (!stockResponse) throw new Error("Unable to fetch nearby stock");

        // Filter the response based on the required quantity
        const filteredStock = stockResponse.filter(
          (data) => parseInt(data.stock_qty, 10) >= required_quantity
        );

        // Map stock data into branches
        this.branches_with_stock_data = response.data
          .map((branch) => {
            const branchStockData = filteredStock.find(
              (stock) => stock.site_id === branch.id
            );
            if (!branchStockData) return null;
            return {
              branch,
              entered_quantity: required_quantity,
              available_stock: parseInt(branchStockData.stock_qty, 10),
            };
          })
          .filter((branch) => branch !== null); // Remove branches with no stock
      } catch (err) {
        const { $customClientErrorHandler } = useNuxtApp();
        let errorMeta = {productCode: product_code, quantity: required_quantity, stockStoreError: true} 
        
        $customClientErrorHandler(
          err,
          'high',
          'Stock Store Error - trying to fetch nearby stock using getNearByStock()',
          errorMeta
        );
        return err;
      }
    },

    checkCurrentBranchInSearchList(){
      const branchStore = useBranchStore();
      const currentBranchId = branchStore.lastSavedBranch?.id;
      const selectedBranch = this.branches_with_stock_data.find(branchWithStockData => branchWithStockData.branch.id === currentBranchId);
      if(!selectedBranch) return false;
      this.nearby_stock_search_branch_choice = selectedBranch;
      return true;
    },

    /* Decide eligibility of next day cc */
    isEligibleForNextDayCollection(product: Product) {
      const branchStore = useBranchStore();
      if (!branchStore.is_branch_set || product.direct_ship) return false;
      return product.outOfStockForCollection && !product.outOfStockForDelivery;
    },

    isTrolleyItemOutOfStock(options: {
      product: Product;
      channel: keyof typeof TrolleyChannel;
      quantity: number;
    }) {
      switch (options.channel) {
        case "Collection":
          return options.product.stockDetails!.collection! < options.quantity;
        default:
          return options.product.stockDetails!.delivery! < options.quantity;
      }
    },

    isProductOutOfStock(product: Product) {
      if(!checkNotifyMeOption(product)) return false;
      //---- show notify me for directship product if quantity exceeds stock
      if (product.direct_ship) return product.outOfStockForDelivery;
      //---- show notify me when branch is set and we are sure about collection stock
      return product.outOfStockForCollection && product.outOfStockForDelivery;
    },
  },
});

/* Pinia hot reload */
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useStockStore, import.meta.hot));
}
